/**
 * Discount Preview — State Module
 *
 * Hält den aktuellen Rabattcode-State im Speicher und synchronisiert mit
 * sessionStorage. Reine Daten-Schicht, keine Netzwerk- oder DOM-Operationen.
 *
 * Exponiert: window.__dpState
 *   - state (mutable; codes, pendingVariants, displayDiscounts, pendingCodes)
 *   - readActiveCodes(), writeActiveCodes(codes)
 *   - getActiveCodes(), getPendingCodes(), getDisplayDiscounts()
 *   - deriveDisplayDiscounts(cart)
 *
 * Wird vom Orchestrator (discount-preview.js) konsumiert.
 */
(function () {
  'use strict';

  const STORAGE_KEY_CODES = 'zh_active_discount_codes';

  const state = {
    codes: [],
    pendingVariants: new Set(),
    // Anzeige-Liste für chips/notifications: enthält sowohl user-codes
    // als auch automatic discounts (Smile.io etc.), mit removable-Flag.
    displayDiscounts: [],
    // Aus dem echten Cart abgeleiteter automatischer/kundengebundener
    // Prozent-Rabatt (kein Code), z.B. Smile-VIP "Ultimate 30%". Form:
    // { percent: Number, valueType: 'percentage', targetSelection, title } | null.
    // Kundengebundene Rabatte sind im anonymen Storefront-cartCreate NICHT
    // sichtbar, daher rechnen wir den Preview-Preis clientseitig aus diesem
    // Prozentsatz hoch statt die Storefront-API zu fragen.
    autoDiscount: null,
    // Codes die auf leerem cart als pending vorgemerkt sind. Beim ersten cart:change
    // wo der code applicable wird, wandert er aus pendingCodes raus → renderer
    // spawnt dann den toast. Verhindert dass schon beim pending-apply ein "Rabatt-
    // code aktiv"-toast erscheint obwohl noch keine Items im Cart sind.
    pendingCodes: new Set()
  };

  /** Liest aktive Codes aus sessionStorage. */
  function readActiveCodes() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY_CODES);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function writeActiveCodes(codes) {
    try {
      sessionStorage.setItem(STORAGE_KEY_CODES, JSON.stringify(codes));
    } catch (e) {
      // sessionStorage kann in Private-Mode failen, fail silently
    }
  }

  function getActiveCodes() {
    return state.codes.slice();
  }

  /**
   * True wenn irgendein Rabatt aktiv ist, der eine Preis-Preview rechtfertigt:
   * entweder ein user-Code oder ein automatischer/kundengebundener Cart-Rabatt.
   * Der Renderer nutzt das statt nur state.codes.
   */
  function hasActiveDiscounts() {
    return state.codes.length > 0 || !!state.autoDiscount;
  }

  /**
   * Liest aus einem /cart.js-Response einen automatischen (nicht-code)
   * Prozent-Rabatt, der auf ALLE Line-Items wirkt. Nur dann können wir den
   * Preview-Preis clientseitig sicher hochrechnen (gleicher Prozentsatz auf
   * jedes Produkt). Fixed-Amount- oder teil-zielende Rabatte liefern null,
   * dann zeigen wir bewusst keine Preview statt einen falschen Preis.
   *
   * Hinweis: Shopify liefert für manche App-/Funktions-Rabatte ein leeres
   * type-Feld (""), daher prüfen wir nur type !== 'discount_code' (wie
   * deriveDisplayDiscounts) und NICHT auf einen truthy type-Wert.
   */
  function deriveAutoDiscount(cart) {
    if (!cart) return null;
    const apps = cart.cart_level_discount_applications || [];
    for (let i = 0; i < apps.length; i++) {
      const da = apps[i];
      if (da.type === 'discount_code') continue;
      if (da.value_type !== 'percentage') continue;
      if (da.target_selection && da.target_selection !== 'all') continue;
      const percent = Number(da.value);
      if (!Number.isFinite(percent) || percent <= 0) continue;
      return {
        percent: percent,
        valueType: 'percentage',
        targetSelection: da.target_selection || 'all',
        title: da.title || ''
      };
    }
    return null;
  }

  function getPendingCodes() {
    return Array.from(state.pendingCodes);
  }

  function getDisplayDiscounts() {
    // Bei leerem displayDiscounts-array fallback auf state.codes als removable
    // chips (z.B. pending-codes auf leerem cart vor dem ersten cart-fetch).
    if (state.displayDiscounts.length === 0 && state.codes.length > 0) {
      return state.codes.map((code) => ({ code: code, removable: true }));
    }
    // Defensive deep-copy: Konsument darf inner objects nicht mutieren können.
    return state.displayDiscounts.map((d) => ({ code: d.code, removable: d.removable }));
  }

  /**
   * Leitet aus einem /cart.js-Response die Liste aller anzuzeigenden Discounts
   * ab: code-discounts (applicable=true, removable) plus automatic discounts
   * aus cart_level_discount_applications (nicht entfernbar via discount-input).
   * Deduplizierung über lowercase code/title.
   */
  function deriveDisplayDiscounts(cart) {
    if (!cart) return [];
    const codes = (cart.discount_codes || [])
      .filter((c) => c.applicable)
      .map((c) => ({ code: c.code, removable: true }));

    const seen = new Set(codes.map((d) => (d.code || '').toLowerCase()));

    const autos = (cart.cart_level_discount_applications || [])
      .filter((da) => da.type !== 'discount_code')
      .map((da) => ({ code: da.title || '', removable: false }))
      .filter((d) => d.code && !seen.has(d.code.toLowerCase()));

    return codes.concat(autos);
  }

  window.__dpState = {
    state: state,
    readActiveCodes: readActiveCodes,
    writeActiveCodes: writeActiveCodes,
    getActiveCodes: getActiveCodes,
    getPendingCodes: getPendingCodes,
    getDisplayDiscounts: getDisplayDiscounts,
    deriveDisplayDiscounts: deriveDisplayDiscounts,
    hasActiveDiscounts: hasActiveDiscounts,
    deriveAutoDiscount: deriveAutoDiscount
  };
})();
